import ReactGA from 'react-ga4';

// Initialize Google Analytics with deferred loading
export const initializeGA = (measurementId: string): void => {
  if (!measurementId) {
    console.warn('GA Measurement ID is missing. Analytics will not be tracked.');
    return;
  }

  // Defer analytics initialization to not block main thread
  setTimeout((): void => {
    ReactGA.initialize(measurementId, {
      gaOptions: {
        allowLinker: true,
        cookieDomain: 'auto'
      },
      // Configuration for cross-domain tracking
      gtagOptions: {
        linker: {
          domains: ['shaunscott.com', 'shaunscott.design']
        }
      }
    });
    
    // Track initial pageview after deferred initialization
    ReactGA.send({ hitType: "pageview", page: window.location.pathname + window.location.search });
  }, 0);
};

// Export with the name that App.js is expecting
export const initGA = initializeGA;

// Track page views
export const trackPageView = (path: string): void => {
  ReactGA.send({ hitType: "pageview", page: path });
};

// Export with the name that App.js is expecting
export const logPageView = trackPageView;

// Track events (clicks, downloads, form submissions, etc.)
export const trackEvent = (category: string, action: string, label?: string, value?: number): void => {
  ReactGA.event({
    category, // Required: 'Contact', 'Resume', 'Projects', etc.
    action,   // Required: 'Click', 'Download', 'Submit', etc.
    label,    // Optional: More specific info like 'LinkedIn Click' or 'Resume Download'
    value     // Optional: Numeric value associated with the event
  });
};

// Track form interactions
export const trackFormInteraction = (formName: string, action: string, fieldName?: string): void => {
  trackEvent('Form Interaction', action, `${formName}${fieldName ? ` - ${fieldName}` : ''}`);
};

// Track conversion funnel steps
export const trackConversionStep = (funnelName: string, step: number, stepName: string): void => {
  trackEvent('Conversion Funnel', `Step ${step}`, `${funnelName} - ${stepName}`, step);
};

// Track project engagement
export const trackProjectEngagement = (projectName: string, action: string, description?: string): void => {
  trackEvent('Project Engagement', action, `${projectName}${description ? ` - ${description}` : ''}`);
};

// Track external link clicks
export const trackExternalLink = (linkText: string, destination: string): void => {
  trackEvent('External Link', 'Click', `${linkText} - ${destination}`);
};

// Track download events
export const trackDownload = (fileName: string, fileType: string): void => {
  trackEvent('Download', 'File Download', `${fileName} (${fileType})`);
};

// Track social media interactions
export const trackSocialMedia = (platform: string, action: string): void => {
  trackEvent('Social Media', action, platform);
};

// Track search queries (if applicable)
export const trackSearch = (query: string, resultsCount?: number): void => {
  trackEvent('Search', 'Query', query, resultsCount);
};

// Track errors
export const trackError = (errorType: string, errorMessage: string, errorLocation?: string): void => {
  trackEvent('Error', errorType, `${errorMessage}${errorLocation ? ` - ${errorLocation}` : ''}`);
};

// Track user engagement metrics
export const trackEngagement = (metric: string, value: number, context?: string): void => {
  trackEvent('Engagement', metric, context, value);
};

// Track page performance
export const trackPerformance = (metric: string, value: number, page: string): void => {
  trackEvent('Performance', metric, page, value);
};

// Track accessibility interactions
export const trackAccessibility = (action: string, element: string): void => {
  trackEvent('Accessibility', action, element);
};

// Track mobile-specific interactions
export const trackMobileInteraction = (action: string, element: string): void => {
  trackEvent('Mobile Interaction', action, element);
};

// Track scroll depth
export const trackScrollDepth = (depth: number, page: string): void => {
  trackEvent('Scroll Depth', 'Percentage', page, depth);
};

// Track time on page
export const trackTimeOnPage = (timeInSeconds: number, page: string): void => {
  trackEvent('Time on Page', 'Duration', page, timeInSeconds);
};

// Track video interactions (if applicable)
export const trackVideoInteraction = (videoName: string, action: string, timestamp?: number): void => {
  trackEvent('Video Interaction', action, videoName, timestamp);
};

// Track newsletter signups
export const trackNewsletterSignup = (source: string): void => {
  trackEvent('Newsletter', 'Signup', source);
};

// Track contact form submissions
export const trackContactSubmission = (formType: string, success: boolean): void => {
  trackEvent('Contact Form', success ? 'Success' : 'Error', formType);
};

// Track resume downloads
export const trackResumeDownload = (format: string, source: string): void => {
  trackEvent('Resume Download', format, source);
};

// Track project page views
export const trackProjectView = (projectName: string, source: string): void => {
  trackEvent('Project View', 'Page View', `${projectName} - ${source}`);
};

// Track navigation patterns
export const trackNavigation = (from: string, to: string, method: string): void => {
  trackEvent('Navigation', method, `${from} → ${to}`);
};

// Track device and browser information
export const trackDeviceInfo = (): void => {
  const userAgent = navigator.userAgent;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const isTablet = /iPad|Android(?=.*\bMobile\b)/i.test(userAgent);
  
  let deviceType = 'Desktop';
  if (isTablet) deviceType = 'Tablet';
  else if (isMobile) deviceType = 'Mobile';
  
  trackEvent('Device Info', 'Device Type', deviceType);
  
  // Track browser
  let browser = 'Unknown';
  if (userAgent.includes('Chrome')) browser = 'Chrome';
  else if (userAgent.includes('Firefox')) browser = 'Firefox';
  else if (userAgent.includes('Safari')) browser = 'Safari';
  else if (userAgent.includes('Edge')) browser = 'Edge';
  
  trackEvent('Device Info', 'Browser', browser);
};

// Track screen resolution
export const trackScreenResolution = (): void => {
  const width = window.screen.width;
  const height = window.screen.height;
  const resolution = `${width}x${height}`;
  
  trackEvent('Device Info', 'Screen Resolution', resolution);
};

// Track connection speed (if available)
export const trackConnectionSpeed = (): void => {
  if ('connection' in navigator && navigator.connection) {
    const effectiveType = navigator.connection.effectiveType || 'unknown';
    trackEvent('Device Info', 'Connection Speed', effectiveType);
  }
};

// Track language preferences
export const trackLanguagePreference = (): void => {
  const language = navigator.language || 'unknown';
  trackEvent('Device Info', 'Language', language);
};

// Track timezone
export const trackTimezone = (): void => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  trackEvent('Device Info', 'Timezone', timezone);
};

// Track referrer information
export const trackReferrer = (): void => {
  const referrer = document.referrer || 'direct';
  const isInternal = referrer.includes(window.location.hostname);
  const referrerType = isInternal ? 'internal' : 'external';
  
  trackEvent('Traffic Source', 'Referrer Type', referrerType);
  
  if (!isInternal && referrer !== 'direct') {
    try {
      const referrerDomain = new URL(referrer).hostname;
      trackEvent('Traffic Source', 'External Referrer', referrerDomain);
    } catch (error) {
      trackEvent('Traffic Source', 'External Referrer', 'invalid-url');
    }
  }
};

// Track UTM parameters
export const trackUTMParameters = (): void => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');
  const utmCampaign = urlParams.get('utm_campaign');
  
  if (utmSource) {
    trackEvent('Traffic Source', 'UTM Source', utmSource);
  }
  if (utmMedium) {
    trackEvent('Traffic Source', 'UTM Medium', utmMedium);
  }
  if (utmCampaign) {
    trackEvent('Traffic Source', 'UTM Campaign', utmCampaign);
  }
};

// Track page load performance
export const trackPageLoadPerformance = (): void => {
  if (window.performance && window.performance.timing) {
    const timing = window.performance.timing;
    const loadTime = timing.loadEventEnd - timing.navigationStart;
    const domContentLoaded = timing.domContentLoadedEventEnd - timing.navigationStart;
    const firstByte = timing.responseStart - timing.navigationStart;
    
    trackEvent('Performance', 'Page Load Time', window.location.pathname, loadTime);
    trackEvent('Performance', 'DOM Content Loaded', window.location.pathname, domContentLoaded);
    trackEvent('Performance', 'Time to First Byte', window.location.pathname, firstByte);
  }
};

// Track scroll behavior
export const trackScrollBehavior = (): void => {
  let maxScrollDepth = 0;
  let scrollEvents = 0;
  
  const trackScroll = (): void => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollDepth = Math.round((scrollTop / documentHeight) * 100);
    
    if (scrollDepth > maxScrollDepth) {
      maxScrollDepth = scrollDepth;
    }
    
    scrollEvents++;
    
    // Track milestone scroll depths
    if (scrollDepth >= 25 && scrollDepth < 50) {
      trackEvent('Scroll Depth', '25%', window.location.pathname);
    } else if (scrollDepth >= 50 && scrollDepth < 75) {
      trackEvent('Scroll Depth', '50%', window.location.pathname);
    } else if (scrollDepth >= 75 && scrollDepth < 90) {
      trackEvent('Scroll Depth', '75%', window.location.pathname);
    } else if (scrollDepth >= 90) {
      trackEvent('Scroll Depth', '90%', window.location.pathname);
    }
  };
  
  // Throttle scroll tracking
  let scrollTimeout: NodeJS.Timeout;
  window.addEventListener('scroll', (): void => {
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }
    scrollTimeout = setTimeout(trackScroll, 100);
  });
  
  // Track final scroll depth on page unload
  window.addEventListener('beforeunload', (): void => {
    trackEvent('Scroll Depth', 'Max Depth', window.location.pathname, maxScrollDepth);
    trackEvent('Scroll Behavior', 'Total Scroll Events', window.location.pathname, scrollEvents);
  });
};

// Track click heatmap data
export const trackClickHeatmap = (): void => {
  document.addEventListener('click', (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    const elementType = target.tagName.toLowerCase();
    const elementClass = target.className || 'no-class';
    const elementId = target.id || 'no-id';
    
    // Track clicks on interactive elements
    if (['a', 'button', 'input', 'select', 'textarea'].includes(elementType)) {
      trackEvent('Click Heatmap', elementType, `${elementClass} - ${elementId}`);
    }
  });
};

// Track form field interactions
export const trackFormFieldInteractions = (): void => {
  document.addEventListener('focus', (event: FocusEvent): void => {
    const target = event.target as HTMLElement;
    if (['input', 'select', 'textarea'].includes(target.tagName.toLowerCase())) {
      const form = target.closest('form');
      const formName = form?.id || form?.className || 'unknown-form';
      trackEvent('Form Interaction', 'Field Focus', `${formName} - ${(target as HTMLInputElement).name || target.id || 'unknown-field'}`);
    }
  });
  
  document.addEventListener('blur', (event: FocusEvent): void => {
    const target = event.target as HTMLElement;
    if (['input', 'select', 'textarea'].includes(target.tagName.toLowerCase())) {
      const form = target.closest('form');
      const formName = form?.id || form?.className || 'unknown-form';
      trackEvent('Form Interaction', 'Field Blur', `${formName} - ${(target as HTMLInputElement).name || target.id || 'unknown-field'}`);
    }
  });
};

// Track keyboard navigation
export const trackKeyboardNavigation = (): void => {
  document.addEventListener('keydown', (event: KeyboardEvent): void => {
    if (event.key === 'Tab') {
      trackEvent('Accessibility', 'Keyboard Navigation', 'Tab Key');
    } else if (event.key === 'Enter' && (event.target as HTMLElement).tagName === 'A') {
      trackEvent('Accessibility', 'Keyboard Navigation', 'Enter on Link');
    }
  });
};

// Track mouse vs keyboard usage
export const trackInputMethod = (): void => {
  let mouseUsed = false;
  let keyboardUsed = false;
  
  document.addEventListener('mousedown', (): void => {
    mouseUsed = true;
  });
  
  document.addEventListener('keydown', (): void => {
    keyboardUsed = true;
  });
  
  // Track input method on page unload
  window.addEventListener('beforeunload', (): void => {
    if (mouseUsed && keyboardUsed) {
      trackEvent('Input Method', 'Mixed', window.location.pathname);
    } else if (mouseUsed) {
      trackEvent('Input Method', 'Mouse', window.location.pathname);
    } else if (keyboardUsed) {
      trackEvent('Input Method', 'Keyboard', window.location.pathname);
    }
  });
};

// Initialize all tracking
export const initializeTracking = (): void => {
  trackDeviceInfo();
  trackScreenResolution();
  trackConnectionSpeed();
  trackLanguagePreference();
  trackTimezone();
  trackReferrer();
  trackUTMParameters();
  trackPageLoadPerformance();
  trackScrollBehavior();
  trackClickHeatmap();
  trackFormFieldInteractions();
  trackKeyboardNavigation();
  trackInputMethod();
};

// Export all tracking functions
export const logEvent = trackEvent;
